<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useTranslaterStore } from '@/stores/translater'
import { RouterLink } from 'vue-router'

import UTASmartCaptcha from '@/components/auth/UTASmartCaptcha.vue'
import checkValid from '@/helpers/checkValid'

const authStore         = useAuthStore()
const translaterStore   = useTranslaterStore()

const authProcess       = computed(() => authStore.authProcess)
const authMsg           = computed(() => authStore.authMsg)
const smartCaptchaId    = computed(() => authStore.smartCaptchaId)

const getTranslate      = translaterStore.getTranslate

const UTAAuthLogin      = ref('')
const UTAAuthPass       = ref('')
const formErrors        = ref([])
const scriptErrors      = ref([])
const formInvalids      = ref({})
const loginData         = ref({})
const isFirstTry        = ref(false)
const showPassword      = ref(false)

// Ошибки скрипта каптчи
function setScriptError(error) {

    scriptErrors.value.push(error)
}

function handleSubmit() {

    formErrors.value = []
    formInvalids.value = {} 

    loginData.value = { login: {} }

    if(
        UTAAuthLogin.value &&
        (
            checkValid(UTAAuthLogin.value, 'phone') || 
            checkValid(UTAAuthLogin.value, 'email')
        )
    ) {
        UTAAuthLogin.value.indexOf('@') > -1 ?
            loginData.value.login.email = UTAAuthLogin.value :
            loginData.value.login.phone = UTAAuthLogin.value.replace(/\D/g, '')
    } else {
        formErrors.value.push(getTranslate.AUTH_LOGIN)
        formInvalids.value.UTAAuthLogin = true
    }

    if(UTAAuthPass.value) {
        loginData.value.password = UTAAuthPass.value
    } else {
        formErrors.value.push(getTranslate.AUTH_PASS)
        formInvalids.value.UTAAuthPass = true
    }
    
    if(!window.smartCaptcha) {
        formErrors.value.push(getTranslate.SMART_CAPTCHA_ERROR_METHOD)
        formInvalids.value.UTAAuthCaptcha = true
    }

    if(formErrors.value.length == 0) {
        
        if(isFirstTry.value) {
            window.smartCaptcha.reset(smartCaptchaId.value)
        }

        window.smartCaptcha.execute(smartCaptchaId.value)
    }
}

function login(smartCaptchaToken) {

    if(typeof smartCaptchaToken === "string" && smartCaptchaToken.length > 0) {
        loginData.value.captcha = {
            yandex: {
                token: smartCaptchaToken
            }
        }
    } else {
        formErrors.value.push(getTranslate.AUTH_CAPTCHA)
        formInvalids.value.UTAAuthCaptcha = true
    }

    if(formErrors.value.length == 0) {
        authStore.sendAuth(loginData.value)

        if(!isFirstTry.value) isFirstTry.value = true
    } else {
        window.smartCaptcha.reset(smartCaptchaId.value)
    }
}

onBeforeUnmount(() => {

    authStore.setAuthMsg({})
})
</script>

<template>
    <div class="uta-auth">
        <div class="uta-auth__header">
            <img src="@/assets/images/logo.png" alt="" class="uta-auth__header-img">
        </div>
        <form action="" @submit.prevent="handleSubmit" class="uta-auth__form">
            <div class="mb-4 uta-alert uta-alert--danger" v-if="scriptErrors.length">
                <span class="material-icons uta-alert__icon">error_outline</span>
                <div class="uta-alert__text" v-for="(error, index) in scriptErrors" :key="error">
                    {{ error }}{{ index !== scriptErrors.length - 1 ? ', ' : '' }} 
                </div>
            </div>
            <div class="mb-4 uta-alert uta-alert--danger" v-if="formErrors.length">
                <span class="material-icons uta-alert__icon">error_outline</span>
                <div class="uta-alert__text">
                    {{ getTranslate.EFormFields }}:
                    <span class="uta-alert__text-item" v-for="(error, index) in formErrors" :key="error">
                        {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                    </span>
                </div>
                <button type="button" class="uta-alert__close" @click="formErrors = [], formInvalids = {}">
                    <span class="material-icons">
                        close
                    </span>
                </button>
            </div>
            <div class="mb-4 uta-alert uta-alert--danger" v-if="'success' in authMsg && !authMsg.success">
                <span class="material-icons uta-alert__icon">error_outline</span>
                <div class="uta-alert__text">
                    {{ authMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="authStore.setAuthMsg({})">
                    <span class="material-icons">
                        close
                    </span>
                </button>
            </div>
            <input 
                class="mb-3 uta-input uta-input--block" 
                :class="{ 'uta-input--invalid' : formInvalids.UTAAuthLogin }"
                type="text" 
                id="UTAAuthLogin" 
                :placeholder="getTranslate['AUTH_LOGIN']"
                @input="delete formInvalids.UTAAuthLogin"
                v-model="UTAAuthLogin"
            >
            <div class="mb-3 uta-input__wrapper uta-password">
                <input 
                    :type="showPassword ? 'text' : 'password'" 
                    class="uta-input uta-input--block uta-input__wrapper-input" 
                    :class="{ 'uta-input--invalid' : formInvalids.UTAAuthPass }"
                    id="UTAAuthPass"
                    :placeholder="getTranslate.AUTH_PASS"
                    @input="delete formInvalids.UTAAuthPass"
                    v-model="UTAAuthPass"
                >
                <span 
                    class="material-icons uta-input__wrapper-icon"
                    @click="showPassword = !showPassword"
                >
                    {{ showPassword ? 'visibility' : 'visibility_off' }} 
                </span>
            </div>
            <button 
                type="submit" 
                class="uta-btn uta-btn--main uta-btn--block uta-btn--lg"
            >
                <template v-if="!authProcess">
                    {{ getTranslate.AUTH_SUBMIT }}
                </template>
                <div class="loader" v-if="authProcess">
                    <div class="spinner-border text-secondary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
            </button>
            <UTASmartCaptcha @getToken="login" @error="setScriptError" />
            <div class="uta-auth__bottom">
                <RouterLink to="/registration" class="uta-auth__bottom-link uta-auth__bottom-link">{{ getTranslate.AUTH_REGISTRATION }}</RouterLink>
                <RouterLink to="/forgot" class="uta-auth__bottom-link uta-auth__bottom-link">{{ getTranslate.AUTH_FORGOT }}</RouterLink>
            </div>
        </form>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/auth';
</style>